import {Box, Button, Divider, Drawer, IconButton, Stack, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {useContext, useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, ExternalLinkIcon, Menu01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../../context/SignInContext";
import {c, maxW, purpleGradient, purpleGradientHover} from "../landingTokens";
import {openLiveCall} from "./liveTestEvents";
import logo from "../../../assets/logo.svg";

const NAV = [
  {label: "How it works", href: "#works"},
  {label: "Capabilities", href: "#caps"},
  {label: "Campaigns", href: "#campaigns"},
  {label: "Integrations", href: "#integrations"},
  {label: "Specs", href: "#specs"},
  {label: "FAQ", href: "#faq"},
];

export const LandingHeader = () => {
  const {currentUser} = useContext(SignInContext);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        bgcolor: "rgba(248,249,252,0.78)",
        backdropFilter: "saturate(180%) blur(20px)",
        borderBottom: `1px solid ${c.line}`,
      }}
    >
      <Stack
        component="nav"
        direction="row"
        alignItems="center"
        gap={{xs: 2, md: 2, lg: 3}}
        sx={{maxWidth: maxW, mx: "auto", height: {xs: 62, md: 70}, px: 2.75}}
      >
        <Box
          component={Link}
          to="/"
          aria-label="OpenBrain home"
          sx={{display: "inline-flex", alignItems: "center", gap: 1.1, color: c.ink, textDecoration: "none"}}
        >
          <Box component="img" src={logo} alt="" sx={{width: 20, height: 24, objectFit: "contain"}} />
          <Typography sx={{fontSize: 18, fontWeight: 700, letterSpacing: "-0.035em", color: "inherit"}}>
            OpenBrain
          </Typography>
        </Box>

        <Stack direction="row" gap={2.5} sx={{flex: 1, display: {xs: "none", lg: "flex"}}}>
          {NAV.map((item) => (
            <Typography
              key={item.href}
              component="a"
              href={item.href}
              sx={{fontSize: "13px", whiteSpace: "nowrap", color: c.inkSoft, textDecoration: "none", transition: "color 180ms ease", "&:hover": {color: c.purpleInk}}}
            >
              {item.label}
            </Typography>
          ))}
        </Stack>

        <Box sx={{flex: {xs: 1, lg: "none"}}} />

        <Typography
          component="a"
          href="https://docs.openbrain.io"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "13px",
            color: c.inkSoft,
            textDecoration: "none",
            display: {xs: "none", sm: "inline-flex"},
            alignItems: "center",
            gap: 0.5,
            "&:hover": {color: c.purpleInk},
          }}
        >
          Docs
          <HugeiconsIcon icon={ExternalLinkIcon} size={13} />
        </Typography>

        <Typography
          component={Link}
          to={currentUser ? "/projects" : "/login"}
          sx={{fontSize: "13px", color: c.inkSoft, textDecoration: "none", display: {xs: "none", sm: "block"}, "&:hover": {color: c.purpleInk}}}
        >
          {currentUser ? "My Projects" : "Sign in"}
        </Typography>

        <Button
          onClick={openLiveCall}
          variant="contained"
          sx={{
            background: purpleGradient,
            color: c.white,
            borderRadius: "980px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            px: 2.25,
            py: 1.15,
            boxShadow: "0 10px 22px -14px rgba(98,88,232,0.7)",
            "&:hover": {background: purpleGradientHover, boxShadow: "0 12px 26px -12px rgba(98,88,232,0.8)", transform: "translateY(-1px)"},
          }}
        >
          Test it now
        </Button>

        <IconButton
          aria-label="Open menu"
          onClick={() => setMenuOpen(true)}
          sx={{display: {xs: "inline-flex", lg: "none"}, color: c.ink, ml: 0.5}}
        >
            <HugeiconsIcon icon={Menu01Icon} size={22} />
        </IconButton>
      </Stack>

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{sx: {width: "78%", maxWidth: 320, boxSizing: "border-box", bgcolor: c.bg, px: 3, py: 2.5}}}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
          <Typography sx={{fontSize: 16, fontWeight: 700, letterSpacing: "-0.035em", color: c.ink}}>Menu</Typography>
          <IconButton aria-label="Close menu" onClick={() => setMenuOpen(false)} sx={{color: c.ink}}>
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </IconButton>
        </Stack>

        <Stack direction="column">
          {NAV.map((item) => (
            <Typography
              key={item.href}
              component="a"
              href={item.href}
              onClick={() => setMenuOpen(false)}
              sx={{fontSize: "15px", color: c.ink, textDecoration: "none", py: 1.25, "&:hover": {color: c.purpleInk}}}
            >
              {item.label}
            </Typography>
          ))}

          <Divider sx={{my: 1, borderColor: c.line}} />

          <Typography
            component="a"
            href="https://docs.openbrain.io"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            sx={{fontSize: "15px", color: c.ink, textDecoration: "none", py: 1.25, display: "inline-flex", alignItems: "center", gap: 0.75, "&:hover": {color: c.purpleInk}}}
          >
            Docs
            <HugeiconsIcon icon={ExternalLinkIcon} size={15} />
          </Typography>

          <Typography
            component={Link}
            to={currentUser ? "/projects" : "/login"}
            onClick={() => setMenuOpen(false)}
            sx={{fontSize: "15px", fontWeight: 600, color: c.purpleDeep, textDecoration: "none", py: 1.25, "&:hover": {color: c.purpleInk}}}
          >
            {currentUser ? "My Projects" : "Sign in"}
          </Typography>
        </Stack>
      </Drawer>
    </Box>
  );
};
